import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { useTimezone } from "../contexts/TimezoneContext";
import { DEFAULT_DATE_FILTER } from "../utils/dateUtils";
import { isMobileApp } from "../hooks/useIsMobileApp";

const useFinancialsData = () => {
  const { timezone, formatDate, formatDateTime } = useTimezone();
  // Restore last-used filters (survives navigating to job edit and back).
  const _persisted = (() => {
    try { return JSON.parse(sessionStorage.getItem("financials_filters") || "{}"); } catch { return {}; }
  })();
  const [activeTab, setActiveTab] = useState(_persisted.activeTab || "general");
  const [dateFilter, setDateFilter] = useState(_persisted.dateFilter || DEFAULT_DATE_FILTER);
  // Custom date range — used when dateFilter === "custom". Seeded with today so
  // switching to Custom doesn't produce an empty request before the user picks.
  const _todayStr = (() => {
    const d = new Date(); const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  })();
  const [customStart, setCustomStart] = useState(_persisted.customStart || _todayStr);
  const [customEnd, setCustomEnd] = useState(_persisted.customEnd || _todayStr);
  const [loading, setLoading] = useState(false);

  // Data states for each report
  const [financialData, setFinancialData] = useState(null);
  const [technicianData, setTechnicianData] = useState(null);
  const [jobSourceData, setJobSourceData] = useState(null);
  const [sourceWeeklyData, setSourceWeeklyData] = useState(null);
  const [directJobsData, setDirectJobsData] = useState(null);
  const [taxData, setTaxData] = useState(null);
  const [settlementsData, setSettlementsData] = useState(null);
  // Expenses summary — fed into Financials General tab's Net Profit breakdown
  const [expensesSummary, setExpensesSummary] = useState({ total: 0, count: 0, by_category: [] });
  // Tech balances for the General tab — used to compute the post-fee
  // "base salaries" deduction in the Net Profit Summary card so the on-page
  // numbers match the exported PDF (see GeneralReportTab.jsx).
  const [techBalancesGeneral, setTechBalancesGeneral] = useState([]);

  // Filter states
  const [selectedTechnician, setSelectedTechnician] = useState(_persisted.selectedTechnician || "all");
  const [selectedSource, setSelectedSource] = useState("all");
  const [selectedSettleTech, setSelectedSettleTech] = useState("all");
  // Multi-location filter — applies to every report tab. "all" by default.
  const [selectedLocation, setSelectedLocation] = useState(_persisted.selectedLocation || "all");
  // List of store locations (populated once) so FinancialsPage can render
  // the picker only when there are 2+ locations.
  const [storeLocations, setStoreLocations] = useState([]);

  // Settle Balance
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleTech, setSettleTech] = useState(null);
  const [settleAmount, setSettleAmount] = useState("");
  const [settleNotes, setSettleNotes] = useState("");
  const [settleHistory, setSettleHistory] = useState([]);
  const [settleLoading, setSettleLoading] = useState(false);
  const [settleSign, setSettleSign] = useState("positive");
  // Selective job settlement (Phase 2)
  // selectedJobKeys: { "jobId::techId": jobEntry } — checkbox selection in the
  // Technician jobs table. settleJobs: the entries being settled in the dialog.
  const [selectedJobKeys, setSelectedJobKeys] = useState({});
  const [settleJobs, setSettleJobs] = useState(null);

  // Payout Adjustments
  const [adjAmount, setAdjAmount] = useState("");
  const [adjCategory, setAdjCategory] = useState("gas");
  const [adjNotes, setAdjNotes] = useState("");
  const [adjDate, setAdjDate] = useState("");
  const [adjHistory, setAdjHistory] = useState([]);
  const [adjLoading, setAdjLoading] = useState(false);
  const [adjSign, setAdjSign] = useState("positive");
  const [editingAdj, setEditingAdj] = useState(null);
  const [settleTab, setSettleTab] = useState("settle");

  // Settlements tab state
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [quickTech, setQuickTech] = useState("");
  const [quickAmount, setQuickAmount] = useState("");
  const [quickSign, setQuickSign] = useState("positive");
  const [quickNotes, setQuickNotes] = useState("");
  const [quickLoading, setQuickLoading] = useState(false);

  const [exportingPdf, setExportingPdf] = useState(null);

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dateFilter, customStart, customEnd, selectedTechnician, selectedSource, selectedSettleTech, selectedLocation, timezone]);

  // Persist filters so navigating away (e.g. editing a job from the report)
  // and coming back restores the exact same view.
  useEffect(() => {
    try {
      sessionStorage.setItem("financials_filters", JSON.stringify({
        activeTab, dateFilter, customStart, customEnd, selectedTechnician, selectedLocation,
      }));
    } catch { /* quota — ignore */ }
  }, [activeTab, dateFilter, customStart, customEnd, selectedTechnician, selectedLocation]);

  // Fetch the locations list once. Used by the FinancialsPage location
  // picker (only rendered when storeLocations.length >= 2).
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/store/locations`, getAuthConfig());
        const list = Array.isArray(res.data) ? res.data : (res.data?.locations || []);
        setStoreLocations(list);
      } catch { setStoreLocations([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build a common date query string including custom range when applicable
  const dateQS = () => {
    let qs = `date_filter=${encodeURIComponent(dateFilter)}`;
    if (dateFilter === "custom") {
      if (customStart) qs += `&start_date=${encodeURIComponent(customStart)}`;
      if (customEnd) qs += `&end_date=${encodeURIComponent(customEnd)}`;
    }
    return qs;
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const config = getAuthConfig();
      const tzParam = timezone ? `&tz=${encodeURIComponent(timezone)}` : "";
      const dqs = dateQS();
      // Multi-location filter — appended to every report URL.
      // Backend treats null/"all" as "no location filter".
      const locParam = selectedLocation && selectedLocation !== "all"
        ? `&location_id=${encodeURIComponent(selectedLocation)}`
        : "";

      if (activeTab === "general") {
        const res = await axios.get(`${API}/reports/financial?${dqs}${tzParam}${locParam}`, config);
        setFinancialData(res.data);
        // Pull the expenses summary in parallel — keeps the UI in sync with the
        // Expenses page so Net Profit numbers match across screens.
        try {
          const expRes = await axios.get(`${API}/expenses/summary?${dqs}${tzParam}${locParam}`, config);
          setExpensesSummary(expRes.data || { total: 0, count: 0, by_category: [] });
        } catch {
          setExpensesSummary({ total: 0, count: 0, by_category: [] });
        }
        // Pull tech balances so the General tab can compute the same
        // post-fee "base salaries" deduction the PDF uses (see PDF
        // generation in GeneralReportTab.handleExportPdf).
        try {
          const techRes = await axios.get(`${API}/reports/technician?${dqs}${tzParam}${locParam}`, config);
          setTechBalancesGeneral(techRes.data?.tech_balances || []);
        } catch {
          setTechBalancesGeneral([]);
        }
      } else if (activeTab === "technician") {
        const techParam = selectedTechnician !== "all" ? `&technician_id=${selectedTechnician}` : "";
        const res = await axios.get(`${API}/reports/technician?${dqs}${techParam}${tzParam}${locParam}`, config);
        setTechnicianData(res.data);
        // Prune checkbox selection: keep only keys still present and unsettled
        setSelectedJobKeys(prev => {
          const valid = {};
          (res.data?.jobs || []).forEach(j => {
            const k = `${j.job_id}::${j.technician_id || ""}`;
            if (prev[k] && !j.settled) valid[k] = j;
          });
          return valid;
        });
      } else if (activeTab === "job-source") {
        const sourceParam = selectedSource !== "all" ? `&source_id=${selectedSource}` : "";
        const res = await axios.get(`${API}/reports/job-source?${dqs}${sourceParam}${tzParam}${locParam}`, config);
        setJobSourceData(res.data);
        const weeklyRes = await axios.get(`${API}/reports/job-source/weekly?${dqs}${sourceParam}${tzParam}${locParam}`, config);
        setSourceWeeklyData(weeklyRes.data);
      } else if (activeTab === "direct") {
        const res = await axios.get(`${API}/reports/direct-jobs?${dqs}${tzParam}${locParam}`, config);
        setDirectJobsData(res.data);
      } else if (activeTab === "tax") {
        const res = await axios.get(`${API}/reports/tax?${dqs}${tzParam}${locParam}`, config);
        setTaxData(res.data);
      } else if (activeTab === "settlements") {
        const techParam = selectedSettleTech !== "all" ? `&technician_id=${selectedSettleTech}` : "";
        const res = await axios.get(`${API}/reports/settlements?${dqs}${techParam}${tzParam}${locParam}`, config);
        setSettlementsData(res.data);
      }
    } catch (error) {
      console.error("Report fetch error:", error);
      if (error.response?.status !== 401) {
        toast.error("Failed to load report data");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => `$${(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatLocalDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return formatDate(dateStr, { month: "short", day: "numeric", year: "numeric" });
  };

  // ─── Settle Balance ────────────────────────────────
  // Per-job balance from the tech's perspective: what they collected minus
  // what they're owed. Positive = tech owes company, negative = company pays tech.
  // Additional-tech entries never collect (primary tech does), so collected = 0.
  const jobEntryBalance = (j) =>
    ((j.is_primary_tech ? (j.tech_collected || 0) : 0) - (j.tech_payout || 0));

  const loadSettleHistories = async (techId) => {
    try {
      const res = await axios.get(`${API}/technicians/${techId}/settlements`, getAuthConfig());
      setSettleHistory(res.data);
    } catch { setSettleHistory([]); }
    try {
      const res = await axios.get(`${API}/technicians/${techId}/adjustments`, getAuthConfig());
      setAdjHistory(res.data);
    } catch { setAdjHistory([]); }
  };

  const openSettle = async (tech) => {
    setSettleJobs(null);
    setSettleTech(tech);
    const balance = tech.remaining_balance || tech.owes_company || 0;
    setSettleAmount(Math.abs(balance).toFixed(2));
    setSettleSign(balance >= 0 ? "positive" : "negative");
    setSettleNotes("");
    setSettleTab("settle");
    setAdjAmount(""); setAdjCategory("gas"); setAdjNotes(""); setAdjSign("positive");
    setSettleOpen(true);
    await loadSettleHistories(tech.technician_id);
  };

  // Settle ONLY the checked jobs (all entries must belong to one technician).
  const openSettleForJobs = async (entries) => {
    if (!entries?.length) return;
    const tid = entries[0].technician_id;
    const tb = (technicianData?.tech_balances || []).find(t => t.technician_id === tid)
      || { technician_id: tid, name: entries[0].technician_name || "Unknown", owes_company: 0, remaining_balance: 0, total_settled: 0 };
    const total = entries.reduce((s, j) => s + jobEntryBalance(j), 0);
    setSettleJobs(entries);
    setSettleTech(tb);
    setSettleAmount(Math.abs(total).toFixed(2));
    setSettleSign(total >= 0 ? "positive" : "negative");
    setSettleNotes("");
    setSettleTab("settle");
    setAdjAmount(""); setAdjCategory("gas"); setAdjNotes(""); setAdjSign("positive");
    setSettleOpen(true);
    await loadSettleHistories(tid);
  };

  // Dialog open/close wrapper — clears the selected-jobs context on close so
  // the next "Settle" from the balances table is a normal (non-job) settlement.
  const handleSettleOpenChange = (open) => {
    setSettleOpen(open);
    if (!open) setSettleJobs(null);
  };

  const refreshSettleTechBalance = async (techId) => {
    try {
      const tzParam = timezone ? `&tz=${encodeURIComponent(timezone)}` : "";
      const res = await axios.get(`${API}/reports/technician?${dateQS()}${tzParam}`, getAuthConfig());
      const freshTech = (res.data.tech_balances || []).find(t => t.technician_id === techId);
      if (freshTech) setSettleTech(freshTech);
    } catch { /* silent */ }
  };

  const submitSettle = async () => {
    const jobBased = !!(settleJobs && settleJobs.length);
    // $0 settlements are allowed for job-based settles (no-charge callbacks) —
    // they mark the jobs settled without moving money.
    if (!jobBased && (!settleAmount || parseFloat(settleAmount) === 0)) return;
    setSettleLoading(true);
    try {
      const parsed = Math.abs(parseFloat(settleAmount) || 0);
      const finalAmount = settleSign === "negative" ? -parsed : parsed;
      const res = await axios.post(`${API}/technicians/${settleTech.technician_id}/settlements`, {
        amount: finalAmount,
        notes: settleNotes,
        // Attribute the settlement to the period the user is currently viewing,
        // NOT to "now". Fixes the bug where settling last week's balance from a
        // "Last Week" view would incorrectly shift the settlement into this week.
        date_filter: dateFilter,
        tz: timezone,
        // When using Custom range, pass the exact boundaries so the backend
        // stores them on the settlement record.
        ...(dateFilter === "custom" ? { period_start: customStart, period_end: customEnd } : {}),
        // Selective settlement: only these jobs get marked as settled.
        ...(jobBased ? { job_ids: [...new Set(settleJobs.map(j => j.job_id))] } : {}),
      }, getAuthConfig());
      const markedCount = res.data?.jobs_count || 0;
      toast.success(jobBased
        ? `Settlement recorded — ${settleJobs.length} job${settleJobs.length > 1 ? "s" : ""} settled`
        : markedCount
          ? `Settlement recorded — ${markedCount} job${markedCount > 1 ? "s" : ""} in this period marked settled`
          : "Settlement recorded");
      setSettleAmount(""); setSettleNotes("");
      if (jobBased) {
        // Close the dialog and clear selection so the refreshed table shows
        // the newly-settled badges immediately.
        setSettleJobs(null);
        setSelectedJobKeys({});
        setSettleOpen(false);
        await fetchReportData();
      } else {
        const [histRes] = await Promise.all([
          axios.get(`${API}/technicians/${settleTech.technician_id}/settlements`, getAuthConfig()),
          refreshSettleTechBalance(settleTech.technician_id),
          fetchReportData()
        ]);
        setSettleHistory(histRes.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record settlement");
    } finally {
      setSettleLoading(false);
    }
  };

  const deleteSettlement = async (settlementId) => {
    try {
      await axios.delete(`${API}/technicians/settlements/${settlementId}`, getAuthConfig());
      toast.success("Settlement deleted");
      setSettleHistory(prev => prev.filter(s => s.settlement_id !== settlementId));
      await Promise.all([refreshSettleTechBalance(settleTech.technician_id), fetchReportData()]);
    } catch {
      toast.error("Failed to delete settlement");
    }
  };

  // ─── Adjustments ───────────────────────────────────
  const submitAdjustment = async () => {
    if (!adjAmount || parseFloat(adjAmount) === 0) return;
    setAdjLoading(true);
    try {
      const finalAmount = adjSign === "negative" ? -Math.abs(parseFloat(adjAmount)) : Math.abs(parseFloat(adjAmount));
      const payload = {
        amount: finalAmount,
        category: adjCategory,
        notes: adjNotes,
        adjustment_date: adjDate || undefined
      };

      if (editingAdj) {
        const res = await axios.put(`${API}/technicians/adjustments/${editingAdj.adjustment_id}`, payload, getAuthConfig());
        toast.success("Adjustment updated");
        setAdjHistory(prev => prev.map(a => a.adjustment_id === editingAdj.adjustment_id ? res.data : a));
        setEditingAdj(null);
      } else {
        const res = await axios.post(`${API}/technicians/${settleTech.technician_id}/adjustments`, payload, getAuthConfig());
        toast.success("Payout adjustment added");
        setAdjHistory(prev => [res.data, ...prev]);
      }
      setAdjAmount(""); setAdjNotes(""); setAdjDate("");
      await Promise.all([refreshSettleTechBalance(settleTech.technician_id), fetchReportData()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save adjustment");
    } finally {
      setAdjLoading(false);
    }
  };

  const startEditAdj = (adj) => {
    setEditingAdj(adj);
    setAdjAmount(String(Math.abs(adj.amount)));
    setAdjSign(adj.amount >= 0 ? "positive" : "negative");
    setAdjCategory(adj.category || "other");
    setAdjNotes(adj.notes || "");
    setAdjDate(adj.adjustment_date ? adj.adjustment_date.split("T")[0] : "");
  };

  const cancelEditAdj = () => {
    setEditingAdj(null);
    setAdjAmount(""); setAdjNotes(""); setAdjDate(""); setAdjCategory("gas"); setAdjSign("positive");
  };

  const deleteAdjustment = async (adjustmentId) => {
    try {
      await axios.delete(`${API}/technicians/adjustments/${adjustmentId}`, getAuthConfig());
      toast.success("Adjustment deleted");
      setAdjHistory(prev => prev.filter(a => a.adjustment_id !== adjustmentId));
      await Promise.all([refreshSettleTechBalance(settleTech.technician_id), fetchReportData()]);
    } catch {
      toast.error("Failed to delete adjustment");
    }
  };

  // ─── Export ────────────────────────────────────────

  const exportToPDF = async (reportType, opts = {}) => {
    setExportingPdf(reportType);
    try {
      let url = `${API}/reports/${reportType}/pdf?${dateQS()}`;
      if (timezone) url += `&tz=${encodeURIComponent(timezone)}`;
      if (reportType === 'technician' && selectedTechnician !== 'all') {
        url += `&technician_id=${selectedTechnician}`;
      } else if (reportType === 'job-source' && selectedSource !== 'all') {
        url += `&source_id=${selectedSource}`;
      }
      // Custom PDF template ("default" = let the backend pick the account default)
      if (opts.templateId && opts.templateId !== 'default') {
        url += `&template_id=${encodeURIComponent(opts.templateId)}`;
      }
      // Honor the page-level location filter so the PDF matches on-screen data.
      if (selectedLocation && selectedLocation !== 'all') {
        url += `&location_id=${encodeURIComponent(selectedLocation)}`;
      }
      const filename = `${reportType}_report_${dateFilter}.pdf`;
      const token = localStorage.getItem("token");

      const { downloadOrSharePdf } = await import("../utils/pdfShare");
      const result = await downloadOrSharePdf(url, filename, token);
      if (result.method === "webshare_cancelled") return;
      if (result.method === "native") toast.success("Preparing PDF for sharing...");
      else if (result.method === "webshare") toast.success("PDF shared");
      else toast.success("PDF exported successfully");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    } finally {
      setExportingPdf(null);
    }
  };

  // ─── Email technician report PDF ───────────────────────
  // Server-side: regenerates the same PDF that /reports/technician/pdf
  // produces and emails it via MailerSend with an attachment. Frontend
  // never sees the bytes — keeps the round-trip minimal and the PDF a
  // single source of truth.
  const emailTechnicianReportPdf = async ({ technicianId, toEmail, subject, bodyHtml, templateId }) => {
    try {
      const token = localStorage.getItem("token");
      const payload = {
        technician_id: technicianId,
        date_filter: dateFilter,
        tz: timezone,
      };
      if (templateId && templateId !== "default") payload.template_id = templateId;
      if (dateFilter === "custom") {
        if (customStart) payload.start_date = customStart;
        if (customEnd) payload.end_date = customEnd;
      }
      if (toEmail) payload.to_email = toEmail;
      if (subject) payload.subject = subject;
      if (bodyHtml) payload.body_html = bodyHtml;
      const res = await axios.post(`${API}/reports/technician/email-pdf`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      throw new Error(msg || "Failed to send email");
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) { toast.error("No data to export"); return; }
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map(row => headers.map(h => {
        const val = row[h];
        if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
        return val;
      }).join(","))
    ].join("\n");

    const fullFilename = `${filename}_${dateFilter}.csv`;
    if (isMobileApp() && window.ReactNativeWebView) {
      const base64 = btoa(unescape(encodeURIComponent(csvContent)));
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: "SHARE_FILE",
        payload: { base64, filename: fullFilename, mimeType: 'text/csv' }
      }));
      toast.success("Preparing file for sharing...");
    } else {
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fullFilename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Report exported successfully");
    }
  };

  return {
    // Core
    activeTab, setActiveTab, dateFilter, setDateFilter, loading,
    customStart, setCustomStart, customEnd, setCustomEnd,
    formatCurrency, formatLocalDate, formatDate,
    exportToPDF, exportToCSV, exportingPdf, emailTechnicianReportPdf,

    // General
    financialData,
    expensesSummary,
    techBalancesGeneral,

    // Multi-location filter
    selectedLocation, setSelectedLocation, storeLocations,

    // Technician
    technicianData, selectedTechnician, setSelectedTechnician,

    // Job Source
    jobSourceData, sourceWeeklyData, selectedSource, setSelectedSource,

    // Direct
    directJobsData,
    taxData,

    // Settlements
    settlementsData, selectedSettleTech, setSelectedSettleTech,
    expandedWeeks, setExpandedWeeks,
    quickTech, setQuickTech, quickAmount, setQuickAmount,
    quickSign, setQuickSign, quickNotes, setQuickNotes, quickLoading,
    fetchReportData,

    // Settle dialog
    settleOpen, setSettleOpen: handleSettleOpenChange, settleTech, openSettle,
    settleAmount, setSettleAmount, settleNotes, setSettleNotes,
    settleHistory, settleLoading, settleSign, setSettleSign,
    submitSettle, deleteSettlement,
    settleTab, setSettleTab,

    // Selective job settlement
    selectedJobKeys, setSelectedJobKeys, openSettleForJobs, settleJobs, jobEntryBalance,

    // Adjustments
    adjAmount, setAdjAmount, adjCategory, setAdjCategory,
    adjNotes, setAdjNotes, adjDate, setAdjDate,
    adjHistory, adjLoading, adjSign, setAdjSign,
    editingAdj, submitAdjustment, startEditAdj, cancelEditAdj, deleteAdjustment,
  };
};

export default useFinancialsData;
